import { api } from './operations.generated';
import { usersOGService } from '../users/rtkquery/users.og';
import { getLogger } from '../../libs/logger';
import { createQueryFnWithHeaders, type WithHeaders } from '../graphql.utils';

const logger = getLogger('roomApi');

/** Inline until `pnpm nx run frontend:build:graphql-types` regenerates RTK hooks from operations.graphql. */
const deleteSharedRoomMutation = `
  mutation DeleteSharedRoom($input: DeleteSharedRoomInput!) {
    deleteSharedRoom(input: $input)
  }
`;
/**
 * Enhanced room API with cache update logic.
 * Updates the GetRoomsForUser GraphQL cache and invalidates the REST rooms cache
 * after a successful UpdateRoom mutation so the UI reflects changes immediately.
 */
export const roomApi = api

  .enhanceEndpoints({
    addTagTypes: ['TenantRoom'],
    endpoints: {
      GetRoomsForUser: {
        providesTags: ['TenantRoom'],
      },
      GetRoomsForTenant: {
        providesTags: ['TenantRoom'],
      },
      UpdateRoom: {
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;

            // Update GraphQL GetRoomsForUser cache with the updated room data
            dispatch(
              api.util.updateQueryData(
                'GetRoomsForUser',
                undefined,
                (draft) => {
                  const roomEdge = draft.Room?.getRoomsForUser?.edges?.find(
                    (edge) => edge.node.id === data.updateRoom.id,
                  );
                  if (roomEdge) {
                    Object.assign(roomEdge.node, data.updateRoom);
                  }
                },
              ),
            );

            // Invalidate REST rooms cache to force refetch
            // This ensures useRooms/usePersonalRoom hooks get updated data
            dispatch(usersOGService.util.invalidateTags(['rooms']));
          } catch (error) {
            logger.warn('Failed to update room', {
              method: 'onQueryStarted',
              error,
            });
          }
        },
        invalidatesTags: ['TenantRoom'],
      },
      RemoveRoomMember: {
        invalidatesTags: ['TenantRoom'],
      },
      AddRoomMembers: {
        invalidatesTags: ['TenantRoom'],
      },
      CreateSharedRoom: {
        invalidatesTags: ['TenantRoom'],
      },
    },
  })
  .injectEndpoints({
    endpoints: (build) => ({
      DeleteSharedRoom: build.mutation<
        { deleteSharedRoom: boolean },
        WithHeaders<{ input: { id: string } }>
      >({
        queryFn: createQueryFnWithHeaders<
          { deleteSharedRoom: boolean },
          { input: { id: string } }
        >(deleteSharedRoomMutation),
        invalidatesTags: ['TenantRoom'],
      }),
    }),
    overrideExisting: true,
  });

export const {
  useUpdateRoomMutation,
  useGetRoomsForTenantQuery,
  useRemoveRoomMemberMutation,
  useAddRoomMembersMutation,
  useDeleteSharedRoomMutation,
  useCreateSharedRoomMutation,
} = roomApi;
